import {put, call} from 'redux-saga/effects';
import Identification from "../../entities/identification";
import * as Actions from "../actions/identificationActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type IdentificationAction = {
    identificationId: string;
    identification: Identification;
    identifications: Identification[];
};

export function* fetchOneIdentification(action: IdentificationAction) {
	try {
		const identification: Identification = yield call(api.findOne, 'identification', action.identificationId);

		// Now we can update the user interface
		yield put({type: Actions.IDENTIFICATION_FETCH_ONE_SUCCESS, identificationId: action.identificationId, identification});
	}
	catch(err) {
		yield put({type: Actions.IDENTIFICATION_FETCH_ONE_FAILURE, identificationId: action.identificationId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {IdentificationAction} action
 */
export function* saveIdentification(action: IdentificationAction) {
    // console.debug('⮂ IDENTIFICATION_SAVE saga', action.identification?.id);
    
	if (action.identification === undefined || action.identification === null) {
		console.error('saveIdentification received a null');
		throw new Error('saveIdentification received a null');
	}

	if (!(action.identification instanceof Identification)) {
		action.identification = new Identification(action.identification);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'identification', action.identification);
		if (addResult.isErr()) {
			console.error(`⮂ saveIdentification: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IDENTIFICATION_SAVE_FAILURE, identification: action.identification, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IDENTIFICATION_SAVE, identification: action.identification});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'identification', action.identification.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.IDENTIFICATION_SAVE_FAILURE, identification: action.identification, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {IdentificationAction} action
 * @param {Identification[]} action.identifications
 */
export function* saveManyIdentifications(action: IdentificationAction) {
    // console.debug('⮂ IDENTIFICATION_SAVE_MANY saga', action.identifications.map(identification => identification.id).join(','));
    
    if (!Array.isArray(action.identifications)) {
        console.error('saveManyIdentifications expected an array, received', action.identifications);
        throw new Error('saveManyIdentifications expected an array');
    }

	try {
		const toSave: Identification[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.identifications.length; i++) {
			const identification = action.identifications[i];
			identification.persisted = false;
			toSave.push(identification);
		}

		const addResult = yield call(idb.add, 'identification', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyIdentifications: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IDENTIFICATION_SAVE_MANY_FAILURE, identifications: action.identifications, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IDENTIFICATION_SAVE_MANY, identifications: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.IDENTIFICATION_SAVE_MANY_FAILURE, identifications: action.identifications, status: 500, err});

		console.error(`Error saving ${action.identifications.length} identifications to database`, err);
	}
}

/**
 * @param {IdentificationAction} action
 */
export function* removeIdentification(action: IdentificationAction) {
    const userId = getCurrentUserId();
	if (!userId || action.identification === undefined || action.identification === null || !(action.identification instanceof Identification)) {
			console.error('removeIdentification expected type Identification, received', action.identification);
			throw new Error('removeIdentification expected type Identification');
		}

	try {
		// Mark as deleted
		action.identification.status = 'D';
		action.identification.addedOn = newHRTimestamp();
		action.identification.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'identification', action.identification);
		if (addResult.isErr()) {
			console.error(`⮂ removeIdentification: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IDENTIFICATION_REMOVE_FAILURE, identification: action.identification, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IDENTIFICATION_REMOVE, identification: action.identification});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.IDENTIFICATION_REMOVE_FAILURE, identification: action.identification, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Identification.
	*
	* @param {IdentificationAction} action
	*/
export function* restoreIdentification(action: IdentificationAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreIdentification expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.identification === undefined || action.identification === null) {
        console.error('restoreIdentification expected a Identification, received null');
        throw new Error('restoreIdentification expected a Identification, received null');
    }
    else if (!(action.identification instanceof Identification)) action.identification = new Identification(action.identification); 
    
	try {
		// Mark as restored
		action.identification.status = 'U';
		action.identification.addedOn = newHRTimestamp();
		action.identification.addedBy = userId;
		action.identification.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'identification', action.identification);
		if (addResult.isErr()) {
			console.error(`⮂ restoreIdentification: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IDENTIFICATION_RESTORE_FAILURE, identification: action.identification, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IDENTIFICATION_RESTORE, identification: action.identification});
	}
	catch(err) {
		yield put({type: Actions.IDENTIFICATION_RESTORE_FAILURE, identification: action.identification, status: 500, err});

		console.error('Error restoring from database', err);
	}
}